import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RequireRole } from "../../auth/RequireRole";
import { ApiError, describeError } from "../../api/client";
import type { AdminSegmentSummary } from "../../api/types";
import { DetailGroup } from "../../components/DetailGroup";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { invalidateExact } from "../../state/queryClient";
import { useUnsavedChangesBlocker } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { getSegment, updateSegment } from "./api";
import { NO_CRITERIA_LABEL, describeCriteriaGroups } from "./criteria";
import { SegmentEditForm } from "./SegmentEditForm";
import { ManualContactInclusionPanel } from "./ManualContactInclusionPanel";

type EditState = { status: "idle" } | { status: "pending" } | { status: "error"; message: string };

/** Order-insensitive comparison — toggling a checkbox off then back on can reorder `nivelJerarquico` without changing its members, and that alone must never count as a dirty edit. */
function sameMembersRegardlessOfOrder(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

/**
 * `/segmentos/:segmentId` — routed segment detail (redesign-panel-hubspot-mailgun,
 * PR 13, tasks 13.1/13.2/13.3, design.md §5.4). Replaces the previous
 * internal-state detail panel with a directly addressable route so browser
 * Back/Forward and deep links behave predictably (task 13.3 "Back/Forward
 * context").
 *
 * Nonexistent and foreign-tenant segment IDs render the IDENTICAL local
 * not-found state — the backend's `GetByIdAsync` is scoped by `client_id`
 * and returns the same `404` for both cases (design.md §8 non-disclosing
 * tenant contract), so this screen never needs to distinguish them.
 *
 * panel-segment-crud: segments used to be read-only for every role (no
 * `RequireRole` gate existed anywhere in this screen). An inline "Editar"
 * capability closes that gap, gated to `Operador` — mirrors
 * `TemplateReplaceEditor`'s pattern: `SegmentEditForm` is a plain controlled
 * panel, all state stays owned here, and the typed input is NEVER reset on
 * error, only on success/cancel.
 */
export function SegmentDetailScreen() {
  const { segmentId } = useParams<{ segmentId: string }>();

  const { data, error, loading, refetch } = useQuery<AdminSegmentSummary>({
    key: ["segment", segmentId],
    queryFn: () => getSegment(segmentId as string),
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editNombre, setEditNombre] = useState("");
  const [editNivelJerarquico, setEditNivelJerarquico] = useState<string[]>([]);
  const [editIndustria, setEditIndustria] = useState("");
  const [editState, setEditState] = useState<EditState>({ status: "idle" });

  // Codex automated review on commit c39dcc6 (P2 de accesibilidad, confirmado por el
  // orquestador leyendo el codigo real): al activar "Editar" con teclado o lector de
  // pantalla, el boton enfocado desaparece del DOM (se reemplaza por `SegmentEditForm`,
  // que se enfoca a si mismo en su propio mount effect) sin que nada restaure el foco al
  // boton "Editar" cuando el formulario se cierra -- ni por cancelar ni por completar la
  // edicion con exito. `wasEditingRef` distingue ese cierre (foco SI se restaura) del
  // montaje inicial de la pantalla (foco NO se roba de donde sea que el operador ya
  // estaba, ya que en ese punto nunca hubo edicion en curso).
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (wasEditingRef.current && !isEditing) {
      editButtonRef.current?.focus();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const startEdit = useCallback(() => {
    if (!data) return;
    setEditNombre(data.nombre);
    setEditNivelJerarquico(data.criterio.nivelJerarquico ?? []);
    setEditIndustria(data.criterio.industria ?? "");
    setEditState({ status: "idle" });
    setIsEditing(true);
  }, [data]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditState({ status: "idle" });
  }, []);

  const toggleEditNivel = useCallback((value: string) => {
    setEditNivelJerarquico((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const handleEditSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!segmentId) return;
      // Guards a duplicate submission even if the disabled button somehow
      // still dispatched a second click (defense in depth, same pattern
      // `TemplateReplaceEditor`/create forms already use).
      if (editState.status === "pending") return;
      setEditState({ status: "pending" });
      try {
        await updateSegment(segmentId, {
          nombre: editNombre,
          criterio: {
            nivelJerarquico: editNivelJerarquico.length > 0 ? editNivelJerarquico : null,
            industria: editIndustria.trim() === "" ? null : editIndustria,
          },
        });
        setIsEditing(false);
        setEditState({ status: "idle" });
        // Codex automated review on commit 2d6fb6a (P2, confirmed by the
        // orchestrator reading the code): `refetch()` alone only refreshes
        // THIS screen's own `["segment", segmentId]` query — it never
        // invalidated `["segments"]` (`SegmentsScreen`'s list) or
        // `["segment-options"]` (`SegmentSelector`'s dropdown in the
        // campaign editor). Editing a segment's nombre/criterio could leave
        // both showing stale data until something else happened to trigger
        // that specific revalidation. Same mechanism `SegmentCreateForm`'s
        // success handler already uses (`SegmentsScreen.tsx`), reused here.
        invalidateExact([["segments"]]);
        invalidateExact([["segment-options"]]);
        refetch();
      } catch (error) {
        // El input tipeado NUNCA se descarta en error — solo en exito/cancelar
        // (mismo contrato que TemplateReplaceEditor).
        setEditState({
          status: "error",
          message: error instanceof ApiError ? error.message : "No se pudo actualizar el segmento.",
        });
      }
    },
    [segmentId, editNombre, editNivelJerarquico, editIndustria, editState.status, refetch],
  );

  // Revision automatica de Codex sobre los commits ff2b3ed/7185ce1 (defecto P2 confirmado por el
  // orquestador leyendo el codigo real): el link "← Volver a Segmentos" no tenia ningun guard --
  // si el operador tenia el formulario de edicion abierto con cambios tipeados, un clic en ese
  // link (o Back/refresh del navegador) desmontaba el formulario sin aviso, perdiendo el borrador
  // en silencio. Mismo mecanismo que `TemplatesScreen`'s "Ver detalle" guard ya usa
  // (`useUnsavedChangesBlocker` + `confirmLeave()`), pero comparando contra los datos ORIGINALES
  // del segmento (no solo "hay contenido tipeado") -- abrir el formulario sin cambiar nada
  // todavia no cuenta como sucio.
  const isEditDirty =
    isEditing &&
    !!data &&
    (editNombre !== data.nombre ||
      !sameMembersRegardlessOfOrder(editNivelJerarquico, data.criterio.nivelJerarquico ?? []) ||
      editIndustria !== (data.criterio.industria ?? ""));
  const { confirmLeave } = useUnsavedChangesBlocker(isEditDirty);

  const isNotFound = error instanceof ApiError && (error.kind === "notFound" || error.kind === "validation");

  // Codex automated review on PR #156 (P2, `SegmentDetailScreen.tsx:62`, fix
  // before merge per Jose Luis Arcila): the not-found branch below only
  // protects THIS render — it never evicted the entry from `queryClient`'s
  // SHARED cache. Navigating away and back to the same URL re-initializes a
  // NEW `useQuery` instance straight from that still-cached (now stale)
  // entry with no error yet, briefly (or indefinitely, if the revalidation
  // fails/is slow) showing the deleted segment again. `invalidateExact` also
  // notifies this SAME instance's own subscriber, which immediately
  // refetches (confirming the same 404 again) — the ref below fires the
  // eviction exactly ONCE per `segmentId` so that bounce can never loop.
  const evictedForSegmentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isNotFound && segmentId && evictedForSegmentIdRef.current !== segmentId) {
      evictedForSegmentIdRef.current = segmentId;
      invalidateExact([["segment", segmentId]]);
    }
  }, [isNotFound, segmentId]);

  // Codex automated review on PR #156 (P2, `SegmentDetailScreen.tsx:44`, fix
  // before merge per Jose Luis Arcila): the previous fix only covered
  // `loading` (`useQuery`'s `phase === "loading"`) — but on an UNCACHED
  // first visit (the most common case), the hook's real initial phase is
  // `idle` (no data, no error, `loading` still `false`); a passive effect
  // flips it to `loading` only AFTERWARD. `RouteFocus.tsx`'s `useEffect`
  // runs ONCE, synchronously, at the moment of THIS initial render — so the
  // heading must exist for BOTH the genuine `loading` phase AND this
  // pre-fetch `idle` tick, not just the former.
  const isPending = loading || (!data && error === undefined);

  if (isPending) {
    return (
      <>
        <h1>Segmento</h1>
        <p role="status">Cargando segmento…</p>
      </>
    );
  }

  // redesign-panel-hubspot-mailgun, PR 16 slice A1 (findings #2/#4, Engram
  // #725): a FRESH not-found/malformed-ID response must always win over any
  // stale cached `data` from an earlier successful fetch — checked BEFORE
  // the `!data` branch below. Before this fix, `if (!data)` alone let a
  // segment deleted after this screen was first loaded keep showing its
  // stale cached detail indefinitely, because `data` stayed truthy and the
  // fresh `error` was never consulted. A malformed segment ID in the URL
  // gives the backend's own `400` ("validation" kind, `classifyHttpStatus`)
  // rather than a `404` — mapped to the SAME local not-found state as a
  // genuine 404, since retrying a permanently-malformed ID can never
  // succeed either way (same non-disclosing-tenant spirit design.md already
  // applies to nonexistent/foreign-tenant IDs: an ID that will never
  // resolve, regardless of the exact reason).
  if (isNotFound) {
    return (
      <section aria-label="Segmento no encontrado">
        <h1>Segmento no encontrado</h1>
        <p>El segmento solicitado no existe o no está disponible para esta cuenta.</p>
        <Link to="/segmentos">← Volver a Segmentos</Link>
      </section>
    );
  }

  if (!data) {
    return (
      <FeedbackPanel
        variant="error"
        message={describeError(error, "No se pudo cargar el segmento.")}
        onRetry={refetch}
      />
    );
  }

  const criteriaGroups = describeCriteriaGroups(data.criterio);

  return (
    <section aria-label="Detalle de segmento" className="v2-segment-detail">
      {/* Prototipo (`Panel Precision App.dc.html` líneas 887-892): el enlace de
          retorno es un párrafo independiente ANTES del `<header>`, y el propio
          `<header>` alinea el nombre y el botón "Editar" en una fila (no en
          columna) — mismo patrón que `ContactDetailScreen` ya aplica
          (`v2-contact-detail__header`, contacts.css). */}
      <p>
        <Link
          to="/segmentos"
          onClick={(event) => {
            // Un borrador de edicion sucio nunca se descarta en silencio -- nunca autoguarda,
            // solo confirma (mismo contrato que `useUnsavedChangesBlocker`).
            if (!confirmLeave()) {
              event.preventDefault();
            }
          }}
        >
          ← Volver a Segmentos
        </Link>
      </p>
      <header className="v2-segment-detail__header">
        <h1>{data.nombre}</h1>
        <RequireRole role="Operador">
          {!isEditing && (
            <button
              type="button"
              className="v2-u-target-min v2-btn-primary"
              ref={editButtonRef}
              onClick={startEdit}
            >
              Editar
            </button>
          )}
        </RequireRole>
      </header>

      {isEditing && (
        <SegmentEditForm
          segmentId={data.segmentId}
          nombre={editNombre}
          nivelJerarquico={editNivelJerarquico}
          industria={editIndustria}
          error={editState.status === "error" ? editState.message : null}
          pending={editState.status === "pending"}
          onNombreChange={setEditNombre}
          onToggleNivel={toggleEditNivel}
          onIndustriaChange={setEditIndustria}
          onSubmit={(event) => void handleEditSubmit(event)}
          onCancel={cancelEdit}
        />
      )}

      <DetailGroup
        title="Miembros"
        items={[
          {
            label: "Miembros actuales (resueltos)",
            value: <span className="v2-u-tabular-nums">{data.memberCount}</span>,
          },
        ]}
      />

      <DetailGroup
        title="Criterio"
        items={
          criteriaGroups.length > 0
            ? criteriaGroups.map((group) => ({ label: group.label, value: group.value }))
            : [{ label: "Criterio", value: NO_CRITERIA_LABEL }]
        }
      />

      <ManualContactInclusionPanel
        segmentId={data.segmentId}
        onAdded={() => {
          invalidateExact([["segments"]]);
          invalidateExact([["segment-options"]]);
          refetch();
        }}
      />
    </section>
  );
}
